import { useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

export default function Lightbox({ items, index, onClose, onNav }) {
  const item = items[index];

  const prev = useCallback(() => onNav((index - 1 + items.length) % items.length), [index, items.length, onNav]);
  const next = useCallback(() => onNav((index + 1) % items.length), [index, items.length, onNav]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    if (window.__lenis) window.__lenis.stop();
    return () => {
      window.removeEventListener("keydown", onKey);
      if (window.__lenis) window.__lenis.start();
    };
  }, [onClose, prev, next]);

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[80] flex items-center justify-center bg-black/90 p-4 backdrop-blur-md"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        data-testid="gallery-lightbox"
      >
        <button
          className="absolute right-5 top-5 rounded-full border border-gold/40 p-2.5 text-amber-100 transition-colors hover:bg-gold/20"
          onClick={onClose}
          data-testid="lightbox-close-btn"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>

        <button
          className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full border border-gold/40 p-3 text-amber-100 transition-colors hover:bg-gold/20 sm:left-8"
          onClick={(e) => { e.stopPropagation(); prev(); }}
          data-testid="lightbox-prev-btn"
          aria-label="Previous photo"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <button
          className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full border border-gold/40 p-3 text-amber-100 transition-colors hover:bg-gold/20 sm:right-8"
          onClick={(e) => { e.stopPropagation(); next(); }}
          data-testid="lightbox-next-btn"
          aria-label="Next photo"
        >
          <ChevronRight className="h-5 w-5" />
        </button>

        {item && (
          <motion.figure
            key={item.src}
            className="max-w-4xl"
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
          >
            <img src={item.src.replace("w=900", "w=1400")} alt={item.title} className="max-h-[76vh] w-auto rounded-xl border border-gold/30 object-contain" />
            <figcaption className="mt-4 text-center">
              <p className="font-display text-xl text-amber-100">{item.title}</p>
              <p className="mt-1 text-xs uppercase tracking-[0.25em] text-stone-500">
                {index + 1} / {items.length} · Jaiswal Tent House
              </p>
            </figcaption>
          </motion.figure>
        )}
      </motion.div>
    </AnimatePresence>
  );
}
